import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authService } from '../services/authService';
import DismissibleError from './DismissibleError';

type FieldErrors = Record<string, string[]>;

const collectErrors = (error: any): FieldErrors => {
  const data = error?.originalError?.response?.data || error?.response?.data;
  const errors = data?.errors || data;
  if (!errors || typeof errors !== 'object') return {};
  return Object.entries(errors).reduce<FieldErrors>((current, [field, messages]) => {
    current[field] = Array.isArray(messages) ? messages.map(String) : [String(messages)];
    return current;
  }, {});
};

const errorText = (fieldErrors: FieldErrors): string =>
  Object.entries(fieldErrors)
    .flatMap(([field, messages]) =>
      messages.map((message) => `${field === 'non_field_errors' ? 'Reset link' : field.replace(/_/g, ' ')}: ${message}`)
    )
    .join(' ');

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const uid = searchParams.get('uid') || '';
  const token = searchParams.get('token') || '';
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [error, setError] = React.useState('');
  const [fieldErrors, setFieldErrors] = React.useState<FieldErrors>({});

  const invalidLink = !uid || !token;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setMessage('');
    setFieldErrors({});
    if (invalidLink) {
      setError('Invalid or expired password reset link.');
      return;
    }
    if (!newPassword || !confirmPassword) {
      setFieldErrors({ new_password: ['New password and confirmation are required.'] });
      setError('Password reset failed.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setFieldErrors({ confirm_password: ['Passwords do not match.'] });
      setError('Password reset failed.');
      return;
    }
    setLoading(true);
    try {
      const response = await authService.resetPassword(uid, token, newPassword, confirmPassword);
      setMessage(response.message || 'Password reset successful. Please log in with your new password.');
      setNewPassword('');
      setConfirmPassword('');
    } catch (resetError: any) {
      const nextFieldErrors = collectErrors(resetError);
      setFieldErrors(nextFieldErrors);
      setError(resetError?.message || 'Password reset failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="login-page original-login">
      <section className="login-card" aria-labelledby="reset-password-title">
        <img className="login-logo" src="/cybershield-logo.png" alt="CyberShield" />
        <h1 id="reset-password-title">Set a new password</h1>
        <p className="form-intro">Use at least 12 characters with uppercase, lowercase, number, and special character.</p>
        {message && <div className="success-message">{message}</div>}
        {(error || invalidLink) && (
          <DismissibleError
            message={error || 'Invalid or expired password reset link.'}
            onDismiss={() => {
              setError('');
              setFieldErrors({});
            }}
          />
        )}
        {errorText(fieldErrors) && <div className="error-message" role="alert">{errorText(fieldErrors)}</div>}
        {!message && (
          <form onSubmit={submit}>
            <div className="form-group">
              <label htmlFor="new-password">New password</label>
              <input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                autoComplete="new-password"
                disabled={invalidLink}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirm-password">Confirm new password</label>
              <input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                autoComplete="new-password"
                disabled={invalidLink}
                required
              />
            </div>
            <button className="btn btn-primary login-submit" type="submit" disabled={loading || invalidLink}>
              {loading ? 'Resetting...' : 'Reset password'}
            </button>
          </form>
        )}
        <p className="login-footer"><Link to="/login">Back to login</Link></p>
      </section>
    </main>
  );
};

export default ResetPassword;
